import type { Doc, Id } from "@repo/convex/dataModel";
import { Bell } from "lucide-react";
import { AsyncBoundary } from "@/components/ui/async-boundary";
import { NotificationItem } from "./notification-item";

// ============================================================================
// NotificationList
// ============================================================================
// Renders the scrollable list of notifications inside the popover.

interface NotificationListProps {
  notifications: Doc<"notifications">[] | undefined;
  getProjectName: (projectId: Id<"projects">) => string | undefined;
  onNavigate: (notification: Doc<"notifications">) => void;
  onRemove: (notificationId: Doc<"notifications">["_id"]) => void;
}

export function NotificationList({
  notifications,
  getProjectName,
  onNavigate,
  onRemove,
}: NotificationListProps) {
  return (
    <AsyncBoundary
      data={notifications}
      loading={
        <div className="flex items-center justify-center py-8">
          <p className="text-xs text-muted-foreground">Loading...</p>
        </div>
      }
      empty={
        <div
          data-testid="notification-list-empty"
          className="flex flex-col items-center justify-center gap-2 py-8"
        >
          <Bell className="size-8 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground">No notifications</p>
        </div>
      }
    >
      {(notifications) => (
        <ul data-testid="notification-list" className="flex flex-col">
          {notifications.map((notification) => (
            <li key={notification._id}>
              <NotificationItem
                notification={notification}
                projectName={
                  notification.projectId ? getProjectName(notification.projectId) : undefined
                }
                onNavigate={onNavigate}
                onRemove={onRemove}
              />
            </li>
          ))}
        </ul>
      )}
    </AsyncBoundary>
  );
}
